import React, { createContext, useContext, useState, useCallback, ReactNode, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContextType, User, LoginCredentials, UserCreate } from '../types/auth';
import { login as apiLogin, register as apiRegister, getCurrentUser } from '../api';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(
    localStorage.getItem('token')
  );
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(!!token);
  const [loading, setLoading] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const navigate = useNavigate();
  const hasLoadedRef = useRef(false);

  const loadUser = useCallback(async () => {
    // Skip if we've already loaded the user or are currently loading
    if (hasLoadedRef.current || loading) {
      return;
    }

    const storedToken = localStorage.getItem('token');
    if (!storedToken) {
      setIsInitialized(true);
      return;
    }

    if (loading) return;

    console.log('[DEBUG] AuthContext: Loading user data');
    setLoading(true);
    try {
      const userData = await getCurrentUser();
      console.log('[DEBUG] AuthContext: User data loaded', { userId: userData?.id });
      setToken(storedToken);
      setIsAuthenticated(true);
      setUser(userData);
    } catch (error) {
      console.error('Failed to load user data:', error);
      // Clear invalid token
      localStorage.removeItem('token');
      setToken(null);
      setIsAuthenticated(false);
      setUser(null);
    } finally {
      setLoading(false);
      setIsInitialized(true);
    }
  }, [loading]);

  // Load user data on mount and when token changes
  useEffect(() => {
    loadUser();
  }, []);

  const login = async (credentials: LoginCredentials) => {
    try {
      const response = await apiLogin(credentials);
      
      // Store token in localStorage
      localStorage.setItem('token', response.access_token);
      
      setToken(response.access_token);
      setIsAuthenticated(true);
      
      // Load user data after successful login
      const userData = await getCurrentUser();
      setUser(userData);
      
      navigate('/notes');
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    }
  };

  const register = async (userData: UserCreate) => {
    try {
      await apiRegister(userData);
      // After successful registration, log the user in
      await login({ username_or_email: userData.username, password: userData.password });
    } catch (error) {
      console.error('Registration failed:', error);
      throw error;
    }
  };

  const logout = () => {
    // Clear token from localStorage
    localStorage.removeItem('token');
    setToken(null);
    setIsAuthenticated(false);
    setUser(null);
    navigate('/');
  };

  const value: AuthContextType = {
    user,
    token,
    isAuthenticated,
    loading,
    isInitialized,
    loadUser,
    login,
    register,
    logout
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}; 